import { useQueryClient } from '@tanstack/react-query';
import type {
  ClientApiV1CourseUserUserIdResponse,
  ClientApiV1CoursesCourseIdLikeUpdateVariables,
} from '../../resources/@types/uccSchema';
import type { UseReactionMutate } from '../components/pages/profile/CourseItem/CourseItem';
import { getUseCourseUserUserIdQueryQueryKey } from '../queries/useCourseUserUserIdQuery';
import { useCourseCourseIdLikeUpdateMutation } from './useCourseCourseIdLikeUpdateMutation';

export const useHomePageReactionMutate: UseReactionMutate = ({ courseId, profileId }) => {
  const queryClient = useQueryClient();
  const courseUserUserIdQuery = getUseCourseUserUserIdQueryQueryKey({
    userId: String(profileId),
  });

  const { mutate } = useCourseCourseIdLikeUpdateMutation(
    courseId,
    async (variables) => {
      await queryClient.cancelQueries({
        queryKey: courseUserUserIdQuery,
      });

      const previousCoursesResponse =
        queryClient.getQueryData<ClientApiV1CourseUserUserIdResponse>(courseUserUserIdQuery);

      const previousCourses = previousCoursesResponse?.data?.items;

      if (previousCourses) {
        const newCourses = previousCourses.map((previousCourse) => {
          if (previousCourse.id === courseId) {
            return {
              ...previousCourse,
              hasMyReaction: variables.status === 'LIKE',
            };
          }

          return previousCourse;
        });

        queryClient.setQueryData<ClientApiV1CourseUserUserIdResponse>(courseUserUserIdQuery, {
          ...previousCoursesResponse,
          data: {
            ...previousCoursesResponse?.data,
            items: newCourses,
          },
        });
      }

      return { previousCoursesResponse };
    },
    (_error, _variables, context) => {
      if (context?.previousCoursesResponse) {
        queryClient.setQueryData<ClientApiV1CourseUserUserIdResponse>(
          courseUserUserIdQuery,
          context.previousCoursesResponse,
        );
      }
    },
  );

  return {
    reactionMutate: ({ status }: ClientApiV1CoursesCourseIdLikeUpdateVariables) => {
      mutate({ status });
    },
  };
};
